import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { MdSearch, MdImage, MdVideocam } from 'react-icons/md';
import { motion } from 'framer-motion';
import { searchPexelsRequest, updatePexelsType, resetPexels } from '../../redux/slices/pexelsSlice';
import type { RootState } from '../../redux/store';
import type { MediaType } from './types';

interface PexelsMediaSearchProps {
  onSelectMedia?: (url: string, type: MediaType) => void;
}

const PexelsMediaSearch: React.FC<PexelsMediaSearchProps> = ({ onSelectMedia }) => {
  const dispatch = useDispatch();
  const [searchQuery, setSearchQuery] = useState('pizza');
  const [activeTab, setActiveTab] = useState<MediaType>('photos');
  const scrollRef = useRef<HTMLDivElement>(null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const { photos, videos, pagination, loading, currentPage } = useSelector(
    (state: RootState) => state.pexels
  );

  // Initial search on mount
  useEffect(() => {
    dispatch(resetPexels());
    dispatch(searchPexelsRequest({
      query: searchQuery,
      type: activeTab,
      per_page: 20,
      page: 1
    }));
  }, []);

  // Handle scroll for infinite loading
  useEffect(() => {
    const scrollElement = scrollRef.current;
    if (!scrollElement) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = scrollElement;
      const bottom = scrollHeight - scrollTop - clientHeight < 100; // 100px from bottom

      if (bottom && !loading && !isLoadingMore && pagination?.has_next_page) {
        setIsLoadingMore(true);
        dispatch(searchPexelsRequest({
          query: searchQuery,
          type: activeTab,
          per_page: 20,
          page: currentPage + 1,
          append: true
        }));
      }
    };

    scrollElement.addEventListener('scroll', handleScroll);
    return () => scrollElement.removeEventListener('scroll', handleScroll);
  }, [loading, isLoadingMore, pagination, currentPage, searchQuery, activeTab, dispatch]);

  // Reset loading more state when data loads
  useEffect(() => {
    if (!loading) {
      setIsLoadingMore(false);
    }
  }, [loading]);

  const handleSearch = () => {
    if (searchQuery.trim()) {
      dispatch(resetPexels());
      dispatch(searchPexelsRequest({
        query: searchQuery,
        type: activeTab,
        per_page: 20,
        page: 1
      }));
      // Scroll to top
      if (scrollRef.current) {
        scrollRef.current.scrollTop = 0;
      }
    }
  };

  const handleTabChange = (tab: MediaType) => {
    setActiveTab(tab);
    dispatch(updatePexelsType(tab));
    dispatch(resetPexels());
    dispatch(searchPexelsRequest({
      query: searchQuery,
      type: tab,
      per_page: 20,
      page: 1
    }));
    // Scroll to top
    if (scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
  };

  const handleMediaClick = (url: string) => {
    if (onSelectMedia) {
      onSelectMedia(url, activeTab);
    }
  };

  const mediaItems = activeTab === 'photos' ? photos : videos;
  const isEnd = pagination && !pagination.has_next_page;

  // Skeleton loader component
  const SkeletonCard = () => (
    <div className="relative aspect-square rounded-lg overflow-hidden bg-gray-800 animate-pulse">
      <div className="w-full h-full bg-gradient-to-r from-gray-800 via-gray-700 to-gray-800" />
    </div>
  );

  return (
    <div className="flex flex-col h-full">
      {/* Tabs */}
      <div className="flex gap-0 mb-6 border-b border-gray-700">
        <button
          onClick={() => handleTabChange('photos')}
          className={`flex items-center gap-2 px-6 py-3 font-medium transition-colors relative ${
            activeTab === 'photos'
              ? 'text-[#EE4423] border-b-2 border-[#EE4423]'
              : 'text-gray-400 hover:text-white'
          }`}
        >
          <MdImage size={20} />
          Images
        </button>
        {/* <button
          onClick={() => handleTabChange('videos')}
          className={`flex items-center gap-2 px-6 py-3 font-medium transition-colors relative ${
            activeTab === 'videos'
              ? 'text-[#EE4423] border-b-2 border-[#EE4423]'
              : 'text-gray-400 hover:text-white'
          }`}
        >
          <MdVideocam size={20} />
          Videos
        </button> */}
      </div>

      {/* Search Bar */}
      <div className="mb-6">
        <div className="relative">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            placeholder="Search in pexels"
            className="w-full px-4 py-3 pr-12 bg-[#2a2a2a] border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#EE4423]"
          />
          <button
            onClick={handleSearch}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-[#EE4423] transition-colors"
          >
            <MdSearch size={24} />
          </button>
        </div>
      </div>

      {/* Media Grid */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto">
        {loading && mediaItems.length === 0 ? (
          <div className="grid grid-cols-5 gap-4 mb-6">
            {[...Array(20)].map((_, i) => (
              <SkeletonCard key={i} />
            ))}
          </div>
        ) : mediaItems.length > 0 ? (
          <>
            <div className="grid grid-cols-5 gap-4 mb-6">
              {activeTab === 'photos' ? (
                photos.map((photo) => (
                  <motion.div
                    key={photo.id}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => handleMediaClick(photo.src.original)}
                    className="relative aspect-square rounded-lg overflow-hidden cursor-pointer group bg-gray-800"
                  >
                    <img
                      src={photo.src.large}
                      alt={photo.alt || 'Pexels image'}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                    {/* <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all" /> */}
                    {/* <div className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                      <p className="text-white text-xs truncate">{photo.photographer}</p>
                    </div> */}
                  </motion.div>
                ))
              ) : (
                videos.map((video) => (
                  <motion.div
                    key={video.id}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => handleMediaClick(video.video_files[0]?.link || video.image)}
                    className="relative aspect-square rounded-lg overflow-hidden cursor-pointer group bg-gray-800"
                  >
                    <img
                      src={video.image}
                      alt={`Video by ${video.user.name}`}
                      className="w-full h-full object-cover"
                    />
                    {/* <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all flex items-center justify-center">
                      <MdVideocam size={48} className="text-white opacity-70" />
                    </div> */}
                    <div className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                      {/* <p className="text-white text-xs truncate">{video.user.name}</p> */}
                      <p className="text-gray-300 text-xs">{video.duration}s</p>
                    </div>
                  </motion.div>
                ))
              )}
            </div>

            {/* Loading More Skeleton */}
            {isLoadingMore && (
              <div className="grid grid-cols-5 gap-4 mb-6">
                {[...Array(20)].map((_, i) => (
                  <SkeletonCard key={`skeleton-${i}`} />
                ))}
              </div>
            )}

            {/* End of Content Message */}
            {isEnd && (
              <div className="flex flex-col items-center justify-center py-8 text-gray-400">
                <div className="w-16 h-16 mb-4 rounded-full bg-gray-800 flex items-center justify-center">
                  <MdImage size={32} className="opacity-50" />
                </div>
                <p className="text-lg font-medium">You've reached the end</p>
                <p className="text-sm">No more {activeTab} to load</p>
              </div>
            )}
          </>
        ) : (
          <div className="flex flex-col items-center justify-center py-12 text-gray-400">
            <MdSearch size={64} className="mb-4 opacity-50" />
            <p>No {activeTab} found</p>
            <p className="text-sm">Try a different search term</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PexelsMediaSearch;
